import { applyTaxRate, roundedDiv } from '../money-math';

/**
 * Kalkulations-Engine (ADR 0055): reine bigint-Mikro-Rechnung für die
 * Projekt-Kalkulation — Vor-/Nachkalkulation mit Gemeinkosten-Stufung und
 * Intercompany-Sichten. Single Source of Truth für Backend (Read-Model) UND
 * Frontend (Live-Vorschau beim Editieren), analog `money-math.ts`.
 *
 * Formeln (je für PLAN und IST):
 *   MaterialGesamt = ΣMaterial + MGK%(ΣMaterial)
 *   HK             = MaterialGesamt + ΣArbeit + ΣSonderkosten
 *   SK             = HK + VGK%(HK)
 *   ErgebnisHK     = VK − HK        ErgebnisSK = VK − SK
 *   WES%           = ΣMaterial / VK   („WES (o. GK)" — OHNE Materialgemeinkosten,
 *                                     wie im Controlling-Excel des Tech-Leads)
 *
 * Zuschläge werden IMMER auf Summen gerechnet, nie je Zeile (eine Rundung).
 * Abweichung = IST − PLAN (Konvention der abgelösten ProjektControlling-App:
 * bei Kosten ist positiv = überzogen, bei Ergebnissen positiv = besser).
 */

export type CalcEngineSection = 'MATERIAL' | 'LABOR' | 'SPECIAL';

/** Eine Kalkulationszeile mit bereits ermittelten Ist-Werten (vor Override). */
export interface CalcEngineLine {
  section: CalcEngineSection;
  /** Plan-Betrag in Mikro (LABOR: Std × Satz, bereits ausgerechnet). */
  planMicros: bigint;
  /** Automatischer ERP-Ist-Betrag in Mikro; null = keine ERP-Quelle (SPECIAL). */
  actualErpMicros: bigint | null;
  /** Manueller Ist-Override in Mikro — ERSETZT den ERP-Wert (ADR 0055 E2). */
  actualOverrideMicros: bigint | null;
  /** Entity-Schlüssel für die Intercompany-Sichten (aufgelöst, nie leer). */
  entityKey: string;
}

/** Effektiver Ist-Wert einer Zeile: Override ersetzt ERP, sonst ERP, sonst 0. */
export function effectiveActualMicros(line: {
  actualErpMicros: bigint | null;
  actualOverrideMicros: bigint | null;
}): bigint {
  return line.actualOverrideMicros ?? line.actualErpMicros ?? 0n;
}

/** Ein Plan/Ist-Wertepaar mit Abweichung (IST − PLAN). */
export interface PlanActual {
  planMicros: bigint;
  actualMicros: bigint;
  varianceMicros: bigint;
}

const pair = (planMicros: bigint, actualMicros: bigint): PlanActual => ({
  planMicros,
  actualMicros,
  varianceMicros: actualMicros - planMicros,
});

/**
 * Zuschläge auf ganze Cent runden (wie Rechnungen/die abgelöste App) — Mikro-
 * Bruchteile unterhalb eines Cents haben bei GK-Beträgen keine fachliche Bedeutung.
 */
const centRound = (micros: bigint): bigint => roundedDiv(micros, 10_000n) * 10_000n;

/** Zuschlag = Betrag × Satz%, auf ganze Cent gerundet. */
const overhead = (base: bigint, pct: number): bigint => centRound(applyTaxRate(base, pct));

const addPair = (a: PlanActual, b: PlanActual): PlanActual =>
  pair(a.planMicros + b.planMicros, a.actualMicros + b.actualMicros);

/** Anteil in % (1 Nachkommastelle); null, wenn der Nenner fehlt/0 ist. */
export function pctOf(part: bigint, whole: bigint | null): number | null {
  if (whole === null || whole === 0n) return null;
  return Number(roundedDiv(part * 1000n, whole)) / 10;
}

/** Vollständiger Summenblock einer Sicht (Gesamt oder je Entity). */
export interface CalcSummary {
  material: PlanActual;
  materialOverhead: PlanActual;
  materialTotal: PlanActual;
  labor: PlanActual;
  special: PlanActual;
  hk: PlanActual;
  salesOverhead: PlanActual;
  sk: PlanActual;
  /** Erlös der Sicht (Gesamt/DE: VK; AT: IC-Verrechnungspreis). null = nicht gepflegt. */
  revenuePlanMicros: bigint | null;
  revenueActualMicros: bigint | null;
  /** Ergebnis = Erlös − SK bzw. − HK; null ohne Erlös. */
  resultSkPlanMicros: bigint | null;
  resultSkActualMicros: bigint | null;
  resultHkPlanMicros: bigint | null;
  resultHkActualMicros: bigint | null;
  /** Kennzahlen in % (1 Nachkommastelle); null ohne Erlös. */
  wesPctPlan: number | null;
  wesPctActual: number | null;
  resultSkPctPlan: number | null;
  resultSkPctActual: number | null;
  resultHkPctPlan: number | null;
  resultHkPctActual: number | null;
}

export interface CalcOptions {
  /** Materialgemeinkosten in % (z. B. 12,5). */
  materialOverheadPct: number;
  /** Vertriebsgemeinkosten in % auf die Herstellkosten (z. B. 35). */
  salesOverheadPct: number;
  /** Plan-Verkaufspreis in Mikro (null = nicht gepflegt). */
  planRevenueMicros: bigint | null;
  /** Ist-Verkaufspreis in Mikro (= Projekt-Auftragswert; null = nicht gepflegt). */
  actualRevenueMicros: bigint | null;
}

/** Summiert Zeilen eines Bereichs zu einem Plan/Ist-Paar. */
function sumSection(lines: CalcEngineLine[], section: CalcEngineSection): PlanActual {
  let plan = 0n;
  let actual = 0n;
  for (const line of lines) {
    if (line.section !== section) continue;
    plan += line.planMicros;
    actual += effectiveActualMicros(line);
  }
  return pair(plan, actual);
}

/** Baut den Summenblock aus Bereichs-Summen + Zuschlägen + Erlös. */
function buildSummaryFromSections(
  material: PlanActual,
  labor: PlanActual,
  special: PlanActual,
  materialOverhead: PlanActual,
  salesOverhead: PlanActual,
  revenuePlan: bigint | null,
  revenueActual: bigint | null,
): CalcSummary {
  const materialTotal = addPair(material, materialOverhead);
  const hk = addPair(addPair(materialTotal, labor), special);
  const sk = addPair(hk, salesOverhead);
  const result = (revenue: bigint | null, cost: bigint) => (revenue === null ? null : revenue - cost);
  return {
    material,
    materialOverhead,
    materialTotal,
    labor,
    special,
    hk,
    salesOverhead,
    sk,
    revenuePlanMicros: revenuePlan,
    revenueActualMicros: revenueActual,
    resultSkPlanMicros: result(revenuePlan, sk.planMicros),
    resultSkActualMicros: result(revenueActual, sk.actualMicros),
    resultHkPlanMicros: result(revenuePlan, hk.planMicros),
    resultHkActualMicros: result(revenueActual, hk.actualMicros),
    // „WES (o. GK)": Fertigungsmaterial OHNE MGK im Verhältnis zum Erlös.
    wesPctPlan: pctOf(material.planMicros, revenuePlan),
    wesPctActual: pctOf(material.actualMicros, revenueActual),
    resultSkPctPlan: revenuePlan === null ? null : pctOf(revenuePlan - sk.planMicros, revenuePlan),
    resultSkPctActual: revenueActual === null ? null : pctOf(revenueActual - sk.actualMicros, revenueActual),
    resultHkPctPlan: revenuePlan === null ? null : pctOf(revenuePlan - hk.planMicros, revenuePlan),
    resultHkPctActual: revenueActual === null ? null : pctOf(revenueActual - hk.actualMicros, revenueActual),
  };
}

/** Gesamt-Sicht (Kundensicht, ohne Intercompany-Verrechnung). */
export function buildTotalSummary(lines: CalcEngineLine[], opts: CalcOptions): CalcSummary {
  const material = sumSection(lines, 'MATERIAL');
  const labor = sumSection(lines, 'LABOR');
  const special = sumSection(lines, 'SPECIAL');
  const materialOverhead = pair(
    overhead(material.planMicros, opts.materialOverheadPct),
    overhead(material.actualMicros, opts.materialOverheadPct),
  );
  const materialTotal = addPair(material, materialOverhead);
  const hkPlan = materialTotal.planMicros + labor.planMicros + special.planMicros;
  const hkActual = materialTotal.actualMicros + labor.actualMicros + special.actualMicros;
  const salesOverhead = pair(
    overhead(hkPlan, opts.salesOverheadPct),
    overhead(hkActual, opts.salesOverheadPct),
  );
  return buildSummaryFromSections(
    material,
    labor,
    special,
    materialOverhead,
    salesOverhead,
    opts.planRevenueMicros,
    opts.actualRevenueMicros,
  );
}

/**
 * Verteilt einen Gesamt-Zuschlag proportional zu den Basiswerten je Entity —
 * mit Restbetrags-Zuweisung an die letzte Entity, damit die Summe der Anteile
 * EXAKT dem Gesamt-Zuschlag entspricht (ADR 0055 E4; Reihenfolge = Aufrufer).
 */
export function distributeProportional(
  total: bigint,
  bases: bigint[],
): bigint[] {
  const baseSum = bases.reduce((sum, value) => sum + value, 0n);
  const shares: bigint[] = [];
  let assigned = 0n;
  for (let index = 0; index < bases.length; index += 1) {
    if (index === bases.length - 1) {
      shares.push(total - assigned);
    } else {
      const share = baseSum === 0n ? 0n : roundedDiv(total * bases[index], baseSum);
      shares.push(share);
      assigned += share;
    }
  }
  return shares;
}

/**
 * Ein Bestandteil des Verrechnungspreises (Zukauf-Block der Owner-Sicht).
 * Die Marge ist ALIQUOT auf die drei Blöcke verteilt (keine eigene Zeile) —
 * jeder Betrag = Plan-Kosten des Blocks + anteilige Marge.
 */
export interface IcBreakdownComponent {
  kind: 'MATERIAL' | 'LABOR' | 'SPECIAL';
  /** Liefer-Entity, deren Leistung verrechnet wird. */
  supplierKey: string;
  /** Plan-fixierter Betrag in Mikro (Plan = Ist — der Verrechnungspreis steht fest). */
  amountMicros: bigint;
}

/** Eine Entity-Sicht: GuV der Gesellschaft inkl. Intercompany-Verrechnung. */
export interface CalcEngineEntityView {
  entityKey: string;
  summary: CalcSummary;
  /** Verrechnungspreis: Liefer-Entity = ihr Erlös; Owner = Σ Zukäufe als Kostenblock. */
  icTransferPriceMicros: bigint | null;
  /** Owner: die Zukauf-Blöcke, die in den Sonderkosten stecken; sonst leer. */
  icBreakdown: IcBreakdownComponent[];
}

export interface EntityViewOptions extends CalcOptions {
  /** Entity, die den Kundenauftrag hält (Owner) — Erlös = VK, Zukäufe als Kosten. */
  ownerEntityKey: string;
  /** Zielanteil der Liefer-Entities am Plan-Ergebnis HK in % (z. B. 70). */
  profitSplitPct: number;
  /** Eigener Vertriebs-GK-Satz der Owner-Gesellschaft auf IHRE HK (z. B. 7,5). */
  ownerSalesOverheadPct: number;
  /**
   * VERHANDELTER Verrechnungspreis (Mikro) — je Projekt manuell gepflegt.
   * null = der Vorschlag (split% vom Plan-Ergebnis HK) gilt automatisch.
   * Gesetzt: Marge = VP − Σ Plan-Kosten der Liefer-Entities (inkl. MGK-Anteil).
   */
  icTransferPriceMicros: bigint | null;
}

/**
 * Entity-Sichten nach dem Controlling-Excel des Tech-Leads (ADR 0055, Ergänzung
 * 2026-07-20). Der Verrechnungspreis wird BERECHNET und ist Plan-fixiert:
 *
 *   VP(Liefer-Entity) = Material(Plan, inkl. MGK-Anteil) + Arbeit(Plan)
 *                     + Sonderkosten(Plan) + Margenanteil
 *   Margenanteil      = split% × (VK_plan − HK_gesamt_plan), bei mehreren
 *                       Liefer-Entities proportional zu deren Plan-HK verteilt.
 *
 * Liefer-Entity (AT): Erlös = VP (Plan wie Ist — fix), Kosten = eigene Zeilen
 * inkl. MGK-Anteil, KEIN Vertriebs-GK → Plan-Ergebnis = Margenanteil exakt.
 * Owner (DE): Erlös = Kunden-VK; die VP-Bestandteile stehen als Plan-fixierte
 * Zukauf-Blöcke in den Sonderkosten; eigener VGK-Satz auf die eigene HK.
 *
 * Der MGK-Gesamtzuschlag wird per Restbetrags-Verteilung exakt auf die Entities
 * aufgeteilt, daher gilt auf HK-Ebene exakt (Plan wie Ist):
 *   Σ(Entity-Ergebnis HK) = Gesamt-Ergebnis HK.
 * Auf SK-Ebene gilt das bewusst NICHT (eigene VGK-Sätze je Sicht).
 */
/**
 * Vorschlag für den Verrechnungspreis: Σ Plan-Kosten der Liefer-Entities
 * (inkl. MGK-Anteil) + split% × Plan-Ergebnis HK (Gesamt). null ohne
 * Liefer-Entity. Der gepflegte VP am Projekt überschreibt den Vorschlag.
 */
export function suggestIcTransferPrice(
  lines: CalcEngineLine[],
  opts: Omit<EntityViewOptions, 'icTransferPriceMicros'>,
): bigint | null {
  const hasSupplier = lines.some((line) => line.entityKey !== opts.ownerEntityKey);
  if (!hasSupplier) return null;
  const views = buildEntityViews(lines, { ...opts, icTransferPriceMicros: null });
  return views
    .filter((view) => view.entityKey !== opts.ownerEntityKey)
    .reduce((sum, view) => sum + (view.icTransferPriceMicros ?? 0n), 0n);
}

export function buildEntityViews(lines: CalcEngineLine[], opts: EntityViewOptions): CalcEngineEntityView[] {
  const entityKeys = [...new Set(lines.map((line) => line.entityKey))].sort((a, b) =>
    // Owner ans Ende: Liefer-Entity (AT) steht in der Anzeige zuerst.
    a === opts.ownerEntityKey ? 1 : b === opts.ownerEntityKey ? -1 : a.localeCompare(b),
  );
  if (entityKeys.length === 0) return [];

  // Bereichs-Summen je Entity.
  const perEntity = entityKeys.map((key) => {
    const entityLines = lines.filter((line) => line.entityKey === key);
    return {
      key,
      material: sumSection(entityLines, 'MATERIAL'),
      labor: sumSection(entityLines, 'LABOR'),
      special: sumSection(entityLines, 'SPECIAL'),
    };
  });

  // MGK-Gesamtzuschlag (identisch zur Gesamt-Sicht, EINE Rundung) exakt auf die
  // Entities verteilen — Grundlage der HK-Invariante.
  const totalMaterial = perEntity.reduce((acc, e) => addPair(acc, e.material), pair(0n, 0n));
  const mgkTotalPlan = overhead(totalMaterial.planMicros, opts.materialOverheadPct);
  const mgkTotalActual = overhead(totalMaterial.actualMicros, opts.materialOverheadPct);
  const mgkPlanShares = distributeProportional(mgkTotalPlan, perEntity.map((e) => e.material.planMicros));
  const mgkActualShares = distributeProportional(mgkTotalActual, perEntity.map((e) => e.material.actualMicros));

  // Marge der Liefer-Entities: verhandelter VP − Plan-Kosten; ohne gepflegten
  // VP gilt der Vorschlag split% × Plan-Ergebnis HK (ohne Plan-VK: Marge 0,
  // VP = reine Kostenweitergabe).
  const suppliers = perEntity.filter((entity) => entity.key !== opts.ownerEntityKey);
  const hkPlanByEntity = perEntity.map(
    (e, index) => e.material.planMicros + mgkPlanShares[index] + e.labor.planMicros + e.special.planMicros,
  );
  const hkTotalPlan = hkPlanByEntity.reduce((sum, value) => sum + value, 0n);
  const supplierHkPlans = perEntity
    .map((e, index) => ({ key: e.key, hkPlan: hkPlanByEntity[index] }))
    .filter((entry) => entry.key !== opts.ownerEntityKey);
  const supplierCostTotal = supplierHkPlans.reduce((sum, entry) => sum + entry.hkPlan, 0n);
  const totalMarginPlan =
    // != null: deckt auch undefined ab (JS-Aufrufer ohne strikte Typen).
    opts.icTransferPriceMicros != null
      ? opts.icTransferPriceMicros - supplierCostTotal
      : opts.planRevenueMicros === null
        ? 0n
        : centRound(applyTaxRate(opts.planRevenueMicros - hkTotalPlan, opts.profitSplitPct));
  const marginShares = distributeProportional(
    suppliers.length > 0 ? totalMarginPlan : 0n,
    supplierHkPlans.map((entry) => entry.hkPlan),
  );
  const marginByKey = new Map(supplierHkPlans.map((entry, index) => [entry.key, marginShares[index]]));

  // Verrechnungspreis je Liefer-Entity: Plan-Kosten (inkl. MGK-Anteil) + Marge,
  // ALIQUOT auf Material/Abwicklung/Sonderkosten verteilt (centgerundet, Rest
  // auf den letzten Block — Summe = VP exakt). Keine eigene Margen-Zeile.
  const icComponentsByKey = new Map<string, IcBreakdownComponent[]>();
  for (const entity of perEntity) {
    if (entity.key === opts.ownerEntityKey) continue;
    const index = perEntity.indexOf(entity);
    const costBases = [
      entity.material.planMicros + mgkPlanShares[index],
      entity.labor.planMicros,
      entity.special.planMicros,
    ];
    const margin = marginByKey.get(entity.key) ?? 0n;
    const baseSum = costBases.reduce((sum, value) => sum + value, 0n);
    let assigned = 0n;
    const marginParts = costBases.map((base, position) => {
      if (position === costBases.length - 1) return margin - assigned;
      const share = baseSum === 0n ? 0n : centRound(roundedDiv(margin * base, baseSum));
      assigned += share;
      return share;
    });
    icComponentsByKey.set(entity.key, [
      { kind: 'MATERIAL', supplierKey: entity.key, amountMicros: costBases[0] + marginParts[0] },
      { kind: 'LABOR', supplierKey: entity.key, amountMicros: costBases[1] + marginParts[1] },
      { kind: 'SPECIAL', supplierKey: entity.key, amountMicros: costBases[2] + marginParts[2] },
    ]);
  }
  const icPriceByKey = new Map(
    [...icComponentsByKey.entries()].map(([key, components]) => [
      key,
      components.reduce((sum, component) => sum + component.amountMicros, 0n),
    ]),
  );

  return perEntity.map((entity, index) => {
    const isOwner = entity.key === opts.ownerEntityKey;
    if (!isOwner) {
      // Liefer-Entity: Erlös = VP (fix), eigene Kosten, kein Vertriebs-GK.
      const icPrice = icPriceByKey.get(entity.key) ?? 0n;
      const summary = buildSummaryFromSections(
        entity.material,
        entity.labor,
        entity.special,
        pair(mgkPlanShares[index], mgkActualShares[index]),
        pair(0n, 0n),
        icPrice,
        icPrice,
      );
      return { entityKey: entity.key, summary, icTransferPriceMicros: icPrice, icBreakdown: [] };
    }

    // Owner: eigene Zeilen + alle Zukauf-Blöcke (Plan-fixiert) in den Sonderkosten.
    const icBreakdown = [...icComponentsByKey.values()].flat();
    const icCost = icBreakdown.reduce((sum, component) => sum + component.amountMicros, 0n);
    const special = addPair(entity.special, pair(icCost, icCost));
    const materialOverhead = pair(mgkPlanShares[index], mgkActualShares[index]);
    const hkPlan = entity.material.planMicros + materialOverhead.planMicros + entity.labor.planMicros + special.planMicros;
    const hkActual =
      entity.material.actualMicros + materialOverhead.actualMicros + entity.labor.actualMicros + special.actualMicros;
    const summary = buildSummaryFromSections(
      entity.material,
      entity.labor,
      special,
      materialOverhead,
      // Eigener VGK-Satz der Owner-Gesellschaft auf IHRE HK (inkl. Zukäufe).
      pair(overhead(hkPlan, opts.ownerSalesOverheadPct), overhead(hkActual, opts.ownerSalesOverheadPct)),
      opts.planRevenueMicros,
      opts.actualRevenueMicros,
    );
    return {
      entityKey: entity.key,
      summary,
      icTransferPriceMicros: icCost === 0n && icComponentsByKey.size === 0 ? null : icCost,
      icBreakdown,
    };
  });
}
